import React, {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useCallback,
  useMemo,
  useRef
} from 'react';
import {
  supabase,
  getUserProfile,
  getUserCompanies
} from '../utils/supabaseClient';
import { getCompanySubscription } from '../utils/companyClient';
import { useAuth } from './AuthContext';

// --- Define Initial State and Reducer ---

const initialState = {
  loading: true,
  userProfile: null,
  currentCompany: null,
  needsOnboarding: true,
  subscription: null,
  subscriptionStatus: null,
  hasAccess: true, // default true to avoid flash-of-blocked during load
  error: null,
};

// Action types for our reducer
const actionTypes = {
  FETCH_START: 'FETCH_START',
  FETCH_SUCCESS: 'FETCH_SUCCESS',
  FETCH_ERROR: 'FETCH_ERROR',
  SUBSCRIPTION_LOADED: 'SUBSCRIPTION_LOADED',
  RESET: 'RESET',
};

function deriveHasAccess(subscription) {
  return Boolean(subscription?.access_allowed);
}

function userReducer(state, action) {
  switch (action.type) {
    case actionTypes.FETCH_START:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case actionTypes.FETCH_SUCCESS: {
      const { profile, companies } = action.payload;
      const hasCompanies = companies && companies.length > 0;
      const company = hasCompanies ? companies[0]?.companies : null;

      return {
        ...state,
        loading: false,
        userProfile: profile,
        currentCompany: company,
        needsOnboarding: !hasCompanies,
        // Keep hasAccess true until subscription check completes
        hasAccess: true,
        error: null,
      };
    }
    case actionTypes.SUBSCRIPTION_LOADED: {
      const { subscription } = action.payload;
      const status = subscription?.status ?? null;
      return {
        ...state,
        subscription,
        subscriptionStatus: status,
        hasAccess: deriveHasAccess(subscription),
      };
    }
    case actionTypes.FETCH_ERROR:
      return {
        ...initialState,
        loading: false,
        error: action.payload.error,
      };
    case actionTypes.RESET:
      return { ...initialState };
    default:
      throw new Error(`Unhandled action type: ${action.type}`);
  }
}

// --- Create Context ---

const UserContext = createContext(undefined);

// --- Create Provider Component ---

export const UserProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [state, dispatch] = useReducer(userReducer, initialState);
  const lastSubFetchRef = useRef(0);

  const fetchSubscriptionStatus = useCallback(async (company) => {
    if (!company?.id) return;

    try {
      const subscription = await getCompanySubscription(company.id);
      dispatch({
        type: actionTypes.SUBSCRIPTION_LOADED,
        payload: { subscription },
      });
    } catch (err) {
      console.error('Error fetching subscription status:', err);
      // On error, deny access — user can refresh to retry
      dispatch({
        type: actionTypes.SUBSCRIPTION_LOADED,
        payload: { subscription: null },
      });
    }
  }, []);

  const fetchUserData = useCallback(async () => {
    if (!user || !isAuthenticated) {
      dispatch({ type: actionTypes.RESET });
      return;
    }

    dispatch({ type: actionTypes.FETCH_START });

    try {
      const [profile, companies] = await Promise.all([
        getUserProfile(),
        getUserCompanies(),
      ]);

      dispatch({
        type: actionTypes.FETCH_SUCCESS,
        payload: { profile, companies },
      });

      // After user data loads, check subscription status
      const hasCompanies = companies && companies.length > 0;
      const company = hasCompanies ? companies[0]?.companies : null;
      if (company) {
        fetchSubscriptionStatus(company);
      }
    } catch (error) {
      console.error('UserContext: Error fetching user data:', error);
      dispatch({
        type: actionTypes.FETCH_ERROR,
        payload: { error: error.message },
      });
    }
  }, [user, isAuthenticated, fetchSubscriptionStatus]);

  // Fetch data when user ID changes
  useEffect(() => {
    if (user?.id) {
      fetchUserData();
    } else {
      dispatch({ type: actionTypes.RESET });
    }
  }, [user?.id]);

  // Re-fetch subscription status when tab regains focus in case an admin changed access.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (!state.currentCompany) return;

      // 5s cooldown debounce
      const now = Date.now();
      if (now - lastSubFetchRef.current < 5000) return;
      lastSubFetchRef.current = now;

      fetchSubscriptionStatus(state.currentCompany);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [state.currentCompany, fetchSubscriptionStatus]);

  // Memoize the derived `currentUser` object to prevent re-creation on every render
  const currentUser = useMemo(() => {
    if (!user) return null;

    const { userProfile } = state;
    const firstName = userProfile?.first_name || '';
    const lastName = userProfile?.last_name || '';
    const fullName = `${firstName} ${lastName}`.trim();
    const fallbackName = user.email?.split('@')[0] || 'User';
    const displayName = fullName || fallbackName;

    return {
      id: user.id,
      email: user.email || '',
      name: displayName,
      firstName,
      lastName,
      avatarUrl: userProfile?.avatar_url ||
        `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=3b82f6&color=fff&size=128`,
    };
  }, [user, state.userProfile]);

  const contextValue = useMemo(() => ({
    ...state,
    currentUser,
    refreshUserData: fetchUserData,
  }), [state, currentUser, fetchUserData]);

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
};

// --- Custom Hook for easy consumption ---

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
